// Conciliación de tarjetas — importa las liquidaciones de Datafast/Medianet (Excel),
// las cruza automáticamente con los pagos con tarjeta del POS y muestra el tablero.
// El parseo del Excel corre 100% en el navegador (SheetJS); la BD solo recibe filas
// ya normalizadas. Fase 2A.
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookText, CreditCard, FileSpreadsheet, Landmark, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, fieldCls, money, pasarelaReversar, posRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

// ------------------------------------------------------------- Tipos RPC -----
interface Importacion {
  id: string; adquirente: string; archivo: string; fecha: string; n_filas: number;
  total_bruto: number; total_neto: number; estado: string; conciliados: number; pendientes: number;
}
interface Liquidacion {
  id: string; adquirente: string; lote: string | null; autorizacion: string | null; ultimos4: string | null;
  marca: string | null; terminal: string | null; fecha_tx: string | null; fecha_deposito: string | null;
  monto_bruto: number; comision: number; iva_comision: number; ret_ir: number; ret_iva: number; monto_neto: number;
  estado: string; diferencia: number; venta_pago_id: string | null;
}
interface ImportResp { importacion_id: string; filas: number }
interface ConciliarResp { conciliados: number; diferencias: number; sin_match: number }
interface ContabilizarResp {
  asiento_id: string; numero: string | number; fecha: string;
  neto: number; comision: number; ret_ir: number; ret_iva: number; bruto: number; reemplazado: boolean;
}
// Fase 2B: cruce del depósito neto del adquirente contra el estado de cuenta bancario.
interface DepositoBanco {
  adquirente: string; fecha_deposito: string | null; n: number; bruto: number; neto_esperado: number;
  banco_fecha: string | null; banco_monto: number | null; banco_concepto: string | null; banco_referencia: string | null;
  diferencia: number; estado: 'en_banco' | 'pendiente';
}

// Registro de transacciones con tarjeta (883): el sistema por dentro, no el
// archivo del banco. Incluye lo que aún no se ha liquidado y los cobros por
// pasarela que se autorizaron SIN venta detrás.
interface Transaccion {
  id: string; origen: 'datafono' | 'pasarela'; modulo: string | null; fecha: string;
  medio: string;   // 902 · 'tarjeta' | 'deuna'
  venta: string | null; caja: string | null; cliente: string | null; monto: number;
  /** Lo que de verdad pasó por la tarjeta, y su diferencia con el pago. */
  monto_cobrado: number | null; diferencia: number;
  forma_pago: string | null; adquirente: string | null; marca: string | null;
  ultimos4: string | null; autorizacion: string | null; lote: string | null;
  terminal: string | null; referencia: string | null; estado: string;
  conciliado: boolean; estado_conciliacion: string | null;
  estado_cobro: string | null;
  alerta: 'reversado_con_venta' | 'sin_venta' | 'monto_distinto' | 'sin_autorizacion' | 'sin_conciliar' | null;
  creado: string;
}

// Fila normalizada que se envía al RPC pos_tarjeta_import_grabar.
interface FilaGrabar {
  lote: string; autorizacion: string; ultimos4: string; marca: string; terminal: string;
  fecha_tx: string; fecha_deposito: string; bruto: number; comision: number;
  iva_comision: number; ret_ir: number; ret_iva: number; neto: number;
}

const ADQUIRENTES = [
  { v: 'datafast', l: 'Datafast' },
  { v: 'medianet', l: 'Medianet' },
];

const ESTADOS_LIQ = [
  { v: '', l: 'Todos' },
  { v: 'conciliado', l: 'Conciliado' },
  { v: 'diferencia', l: 'Diferencia' },
  { v: 'sin_match', l: 'Sin match' },
  { v: 'pendiente', l: 'Pendiente' },
];
const ESTADO_LIQ_CLS: Record<string, string> = {
  conciliado: 'bg-estable-soft text-estable',
  diferencia: 'bg-pendiente-soft text-pendiente',
  sin_match: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  pendiente: 'bg-muted text-muted-foreground',
};
const estadoLabel = (e: string) => ESTADOS_LIQ.find((x) => x.v === e)?.l ?? e;

const ADQUIRENTES_FILTRO = [{ v: '', l: 'Todos' }, ...ADQUIRENTES];

const ALERTAS = [
  { v: '', l: 'Todas las transacciones' },
  { v: 'reversado_con_venta', l: 'Reversado con factura viva' },
  { v: 'sin_venta', l: 'Cobrado sin venta' },
  { v: 'monto_distinto', l: 'Cobrado por otro importe' },
  { v: 'sin_autorizacion', l: 'Sin autorización' },
  { v: 'sin_conciliar', l: 'Sin conciliar' },
];
const ALERTA_CLS: Record<string, string> = {
  reversado_con_venta: 'bg-urgente-soft text-urgente',
  sin_venta: 'bg-urgente-soft text-urgente',
  monto_distinto: 'bg-urgente-soft text-urgente',
  sin_autorizacion: 'bg-pendiente-soft text-pendiente',
  sin_conciliar: 'bg-muted text-muted-foreground',
};
const ALERTA_LABEL: Record<string, string> = {
  reversado_con_venta: 'Reversado · factura viva',
  sin_venta: 'Cobrado sin venta',
  monto_distinto: 'Importe distinto',
  sin_autorizacion: 'Sin autorización',
  sin_conciliar: 'Sin conciliar',
};
const ESTADO_DEP_CLS: Record<string, string> = {
  en_banco: 'bg-estable-soft text-estable',
  pendiente: 'bg-pendiente-soft text-pendiente',
};
const ESTADO_DEP_LABEL: Record<string, string> = { en_banco: '✓ En banco', pendiente: 'Pendiente de depósito' };

// --------------------------------------------------------- Utilidades parseo --
// Normaliza un encabezado (o valor de texto) para comparar sin acentos/puntuación.
const norm = (s: unknown): string =>
  String(s ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();

// Números con coma/punto decimal indistinto (montos que vienen como texto).
const toNum = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  let s = String(v ?? '').trim();
  if (!s) return 0;
  s = s.replace(/[^\d.,-]/g, '');
  if (s.includes(',') && s.includes('.')) {
    s = s.lastIndexOf(',') > s.lastIndexOf('.') ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (s.includes(',')) {
    s = s.replace(',', '.');
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

// Fecha de una celda (Date de SheetJS, serial numérico o texto dd/mm/yyyy | yyyy-mm-dd) → 'YYYY-MM-DD'.
const parseFechaCelda = (v: unknown): string => {
  if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  if (typeof v === 'number' && v > 0) {
    const d = new Date(Date.UTC(1899, 11, 30) + v * 86400000);
    return d.toISOString().slice(0, 10);
  }
  const s = String(v ?? '').trim();
  if (!s) return '';
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (m) {
    const [, d, mo, yRaw] = m;
    const y = yRaw.length === 2 ? `20${yRaw}` : yRaw;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return '';
};

const findIdx = (headers: string[], ...candidatos: string[]): number => {
  for (const c of candidatos) {
    const i = headers.indexOf(norm(c));
    if (i >= 0) return i;
  }
  return -1;
};
const celda = (row: unknown[], i: number): unknown => (i >= 0 ? row[i] : '');
const filaVacia = (row: unknown[] | undefined): boolean => !row || row.every((c) => c === '' || c == null);

const marcaDatafast = (tipo: unknown): string => {
  const t = norm(tipo);
  if (t.includes('VISA')) return 'Visa';
  if (t.includes('MASTER') || t.includes('MAESTRO')) return 'Mastercard';
  if (t.includes('DINERS')) return 'Diners';
  if (t.includes('AMERICAN') || t.includes('AMEX')) return 'Amex';
  return String(tipo ?? '').trim();
};
const marcaMedianet = (tarjeta: unknown, banco: unknown): string => {
  const digitos = String(tarjeta ?? '').replace(/\D/g, '');
  const primero = digitos.charAt(0);
  if (primero === '4') return 'Visa';
  if (primero === '5') return 'Mastercard';
  if (primero === '3') return 'Amex/Diners';
  return String(banco ?? '').trim();
};
const ultimos4Medianet = (tarjeta: unknown): string => String(tarjeta ?? '').replace(/\D/g, '').slice(-4);

// Datafast: encabezados en la fila 1, datos desde la fila 2. Solo estado 'Conciliado'.
function parseDatafast(rows: unknown[][]): FilaGrabar[] {
  if (rows.length < 2) return [];
  const headers = (rows[0] || []).map(norm);
  const iFecha = findIdx(headers, 'FECHA TRX');
  const iLote = findIdx(headers, 'LOTE');
  const iAut = findIdx(headers, 'AUTORIZACION');
  const iTipo = findIdx(headers, 'TIPO TARJETA');
  const iTid = findIdx(headers, 'TID');
  const iBruto = findIdx(headers, 'VALOR TOTAL');
  const iComision = findIdx(headers, 'COMISION');
  const iIvaCom = findIdx(headers, 'COMISION IVA');
  const iRetIr = findIdx(headers, 'RET. FUENTE', 'RET FUENTE');
  const iRetIva = findIdx(headers, 'RET. IVA', 'RET IVA');
  const iNeto = findIdx(headers, 'VALOR PAGADO');
  const iFPago = findIdx(headers, 'FECHA PAGO');
  const iEstado = findIdx(headers, 'ESTADO');
  const out: FilaGrabar[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (filaVacia(row)) continue;
    if (iEstado >= 0 && norm(celda(row, iEstado)) !== 'CONCILIADO') continue;
    out.push({
      lote: String(celda(row, iLote) ?? '').trim(),
      autorizacion: String(celda(row, iAut) ?? '').trim(),
      ultimos4: '',
      marca: marcaDatafast(celda(row, iTipo)),
      terminal: String(celda(row, iTid) ?? '').trim(),
      fecha_tx: parseFechaCelda(celda(row, iFecha)),
      fecha_deposito: parseFechaCelda(celda(row, iFPago)),
      bruto: toNum(celda(row, iBruto)),
      comision: toNum(celda(row, iComision)),
      iva_comision: toNum(celda(row, iIvaCom)),
      ret_ir: toNum(celda(row, iRetIr)),
      ret_iva: toNum(celda(row, iRetIva)),
      neto: toNum(celda(row, iNeto)),
    });
  }
  return out;
}

// Medianet: fila 1 = números de columna, encabezados en la fila 2, datos desde la fila 3.
function parseMedianet(rows: unknown[][]): FilaGrabar[] {
  if (rows.length < 3) return [];
  const headers = (rows[1] || []).map(norm);
  const iFecha = findIdx(headers, 'FechaMovimiento');
  const iTarjeta = findIdx(headers, 'Tarjeta');
  const iBanco = findIdx(headers, 'NombreBancoEmisor');
  const iLote = findIdx(headers, 'Lote');
  const iAut = findIdx(headers, 'Autorizacion');
  const iComercio = findIdx(headers, 'Comercio');
  const iTotal = findIdx(headers, 'Total');
  const iComision = findIdx(headers, 'Comision');
  const iRetIr = findIdx(headers, 'Reten.Fte.', 'Reten Fte');
  const iRetIva = findIdx(headers, 'Reten.Iva', 'Reten Iva');
  const iNeto = findIdx(headers, 'ValorLiquidado');
  const iFPago = findIdx(headers, 'FechaPagoComercio');
  const iEstado = findIdx(headers, 'Estado');
  const out: FilaGrabar[] = [];
  for (let r = 2; r < rows.length; r++) {
    const row = rows[r];
    if (filaVacia(row)) continue;
    if (iEstado >= 0 && norm(celda(row, iEstado)) !== 'CONCILIADO') continue;
    const tarjeta = celda(row, iTarjeta);
    out.push({
      lote: String(celda(row, iLote) ?? '').trim(),
      autorizacion: String(celda(row, iAut) ?? '').trim(),
      ultimos4: ultimos4Medianet(tarjeta),
      marca: marcaMedianet(tarjeta, celda(row, iBanco)),
      terminal: String(celda(row, iComercio) ?? '').trim(),
      fecha_tx: parseFechaCelda(celda(row, iFecha)),
      fecha_deposito: parseFechaCelda(celda(row, iFPago)),
      bruto: toNum(celda(row, iTotal)),
      comision: toNum(celda(row, iComision)),
      iva_comision: 0,
      ret_ir: toNum(celda(row, iRetIr)),
      ret_iva: toNum(celda(row, iRetIva)),
      neto: toNum(celda(row, iNeto)),
    });
  }
  return out;
}

const fmtFecha = (s: string | null) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString(LOCALE) : '—');

export function ConciliacionTarjetas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [tab, setTab] = useState<'importaciones' | 'depositos' | 'transacciones'>('importaciones');

  // --- 1. Importar ------------------------------------------------------------
  const [adquirente, setAdquirente] = useState<'datafast' | 'medianet'>('datafast');
  const [archivo, setArchivo] = useState('');
  const [fileKey, setFileKey] = useState(0);
  const [filas, setFilas] = useState<FilaGrabar[]>([]);
  const [comercios, setComercios] = useState<Record<string, boolean>>({});
  const [parseando, setParseando] = useState(false);

  const resumenComercios = useMemo(() => {
    const m = new Map<string, { n: number; bruto: number }>();
    for (const f of filas) {
      const k = f.terminal || '(sin terminal)';
      const cur = m.get(k) ?? { n: 0, bruto: 0 };
      cur.n += 1; cur.bruto += f.bruto;
      m.set(k, cur);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es'));
  }, [filas]);

  const filasSeleccionadas = useMemo(
    () => filas.filter((f) => comercios[f.terminal || '(sin terminal)'] !== false),
    [filas, comercios],
  );
  const totalBruto = filasSeleccionadas.reduce((s, f) => s + f.bruto, 0);
  const totalNeto = filasSeleccionadas.reduce((s, f) => s + f.neto, 0);

  const onFile = async (file: File | null) => {
    if (!file) return;
    setArchivo(file.name);
    setParseando(true);
    try {
      const buf = await file.arrayBuffer();
      const XLSX = await import('xlsx');
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' });
      const parsed = adquirente === 'datafast' ? parseDatafast(rows) : parseMedianet(rows);
      setFilas(parsed);
      const term = new Set(parsed.map((f) => f.terminal || '(sin terminal)'));
      setComercios(Object.fromEntries([...term].map((t) => [t, true])));
      if (parsed.length === 0) toast.error('No se detectaron filas conciliadas en el archivo');
      else toast.success(`${parsed.length} fila(s) conciliadas detectadas`);
    } catch (e) {
      toast.error(`No se pudo leer el archivo: ${getErrorMessage(e)}`);
      setFilas([]); setComercios({});
    } finally {
      setParseando(false);
    }
  };

  const toggleComercio = (t: string, v: boolean) => setComercios((c) => ({ ...c, [t]: v }));
  const toggleTodos = (v: boolean) => setComercios(Object.fromEntries(resumenComercios.map(([t]) => [t, v])));

  const importar = useMutation({
    mutationFn: async () => {
      const grab = await posRpc<ImportResp>('pos_tarjeta_import_grabar', {
        _data: { adquirente, archivo, filas: filasSeleccionadas },
      });
      const conc = await posRpc<ConciliarResp>('pos_tarjeta_conciliar_auto', { _importacion_id: grab.importacion_id });
      return { grab, conc };
    },
    onSuccess: ({ grab, conc }) => {
      toast.success(
        `Importadas ${grab.filas} fila(s) · ${conc.conciliados} conciliadas · ${conc.diferencias} con diferencia · ${conc.sin_match} sin match`,
      );
      setFilas([]); setComercios({}); setArchivo(''); setFileKey((k) => k + 1);
      void qc.invalidateQueries({ queryKey: ['pos-tarjeta-importaciones'] });
      void qc.invalidateQueries({ queryKey: ['pos-tarjeta-liquidaciones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // --- 2. Importaciones ---------------------------------------------------------
  const { data: importaciones = [], isFetching: fetchImp } = useQuery<Importacion[]>({
    queryKey: ['pos-tarjeta-importaciones'],
    queryFn: () => posRpc<Importacion[]>('pos_tarjeta_importaciones'),
    staleTime: 0,
  });
  const getValImp = useCallback((r: Importacion, key: string): string | number | null => {
    switch (key) {
      case 'adquirente': return r.adquirente;
      case 'archivo': return r.archivo;
      case 'fecha': return r.fecha;
      case 'n_filas': return Number(r.n_filas);
      case 'total_bruto': return Number(r.total_bruto);
      case 'total_neto': return Number(r.total_neto);
      case 'conciliados': return Number(r.conciliados);
      case 'pendientes': return Number(r.pendientes);
      case 'estado': return r.estado;
      default: return null;
    }
  }, []);
  const sortImp = useSort(importaciones, getValImp);
  const filasImp = sortImp.sorted;
  const tImpFilas = filasImp.reduce((s, r) => s + Number(r.n_filas), 0);
  const tImpBruto = filasImp.reduce((s, r) => s + Number(r.total_bruto), 0);
  const tImpNeto = filasImp.reduce((s, r) => s + Number(r.total_neto), 0);

  // --- 3. Detalle / tablero ------------------------------------------------------
  const [importacionSel, setImportacionSel] = useState<Importacion | null>(null);
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const { data: liquidaciones = [], isFetching: fetchLiq } = useQuery<Liquidacion[]>({
    queryKey: ['pos-tarjeta-liquidaciones', importacionSel?.id, estadoFiltro],
    queryFn: () => posRpc<Liquidacion[]>('pos_tarjeta_liquidaciones', {
      _importacion_id: importacionSel!.id, _estado: estadoFiltro || null,
    }),
    enabled: !!importacionSel,
    staleTime: 0,
  });
  const getValLiq = useCallback((r: Liquidacion, key: string): string | number | null => {
    switch (key) {
      case 'fecha_tx': return r.fecha_tx;
      case 'lote': return r.lote;
      case 'autorizacion': return r.autorizacion;
      case 'ultimos4': return r.ultimos4;
      case 'marca': return r.marca;
      case 'terminal': return r.terminal;
      case 'bruto': return Number(r.monto_bruto);
      case 'comision': return Number(r.comision);
      case 'retenciones': return Number(r.ret_ir) + Number(r.ret_iva);
      case 'neto': return Number(r.monto_neto);
      case 'diferencia': return Number(r.diferencia);
      case 'estado': return r.estado;
      default: return null;
    }
  }, []);
  const sortLiq = useSort(liquidaciones, getValLiq);
  const filasLiq = sortLiq.sorted;
  const tLBruto = filasLiq.reduce((s, r) => s + Number(r.monto_bruto), 0);
  const tLComision = filasLiq.reduce((s, r) => s + Number(r.comision), 0);
  const tLRet = filasLiq.reduce((s, r) => s + Number(r.ret_ir) + Number(r.ret_iva), 0);
  const tLNeto = filasLiq.reduce((s, r) => s + Number(r.monto_neto), 0);

  const contabilizar = useMutation({
    mutationFn: (importacionId: string) =>
      posRpc<ContabilizarResp>('pos_tarjeta_contabilizar', { _importacion_id: importacionId }),
    onSuccess: (r) => {
      const titulo = r.reemplazado ? 'Asiento actualizado' : 'Asiento generado';
      toast.success(`${titulo} N° ${r.numero} · neto ${money(r.neto)} · comisión ${money(r.comision)}`);
      void qc.invalidateQueries({ queryKey: ['pos-tarjeta-importaciones'] });
      void qc.invalidateQueries({ queryKey: ['pos-tarjeta-liquidaciones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const onContabilizar = async () => {
    if (!importacionSel) return;
    if (!(await dlg.confirmar('¿Generar el asiento contable de esta liquidación?'))) return;
    contabilizar.mutate(importacionSel.id);
  };

  // --- 4. Depósitos vs banco (Fase 2B) --------------------------------------------
  const [depDesde, setDepDesde] = useState('');
  const [depHasta, setDepHasta] = useState('');
  const [depAdquirente, setDepAdquirente] = useState('');
  const { data: depositos = [], isFetching: fetchDep } = useQuery<DepositoBanco[]>({
    queryKey: ['pos-tarjeta-depositos', depDesde, depHasta, depAdquirente],
    queryFn: () => posRpc<DepositoBanco[]>('pos_tarjeta_depositos', {
      _desde: depDesde || null, _hasta: depHasta || null, _adquirente: depAdquirente || null,
    }),
    enabled: tab === 'depositos',
    staleTime: 0,
  });
  const getValDep = useCallback((r: DepositoBanco, key: string): string | number | null => {
    switch (key) {
      case 'adquirente': return r.adquirente;
      case 'fecha_deposito': return r.fecha_deposito;
      case 'n': return Number(r.n);
      case 'bruto': return Number(r.bruto);
      case 'neto_esperado': return Number(r.neto_esperado);
      case 'banco_fecha': return r.banco_fecha;
      case 'banco_monto': return r.banco_monto == null ? null : Number(r.banco_monto);
      case 'banco_concepto': return r.banco_concepto;
      case 'diferencia': return Number(r.diferencia);
      case 'estado': return r.estado;
      default: return null;
    }
  }, []);
  const sortDep = useSort(depositos, getValDep);
  const filasDep = sortDep.sorted;
  const tDepBruto = filasDep.reduce((s, r) => s + Number(r.bruto), 0);
  const tDepNeto = filasDep.reduce((s, r) => s + Number(r.neto_esperado), 0);
  const tDepBanco = filasDep.reduce((s, r) => s + Number(r.banco_monto ?? 0), 0);

  // --- 5. Transacciones con tarjeta (883) ---------------------------------------
  const hoy = new Date().toISOString().slice(0, 10);
  const [trxDesde, setTrxDesde] = useState(hoy.slice(0, 8) + '01');
  const [trxHasta, setTrxHasta] = useState(hoy);
  const [trxOrigen, setTrxOrigen] = useState('');
  const [trxAlerta, setTrxAlerta] = useState('');
  // 902 · Cada medio se concilia por su lado: De Una no trae autorización ni se
  // liquida en el archivo del adquirente de tarjetas.
  const [trxMedio, setTrxMedio] = useState('');
  const { data: transacciones = [], isFetching: fetchTrx } = useQuery<Transaccion[]>({
    queryKey: ['pos-tarjeta-transacciones', trxDesde, trxHasta, trxOrigen, trxAlerta, trxMedio],
    queryFn: () => posRpc<Transaccion[]>('pos_tarjeta_transacciones', {
      _desde: trxDesde || null, _hasta: trxHasta || null,
      _origen: trxOrigen || null, _alerta: trxAlerta || null, _medio: trxMedio || null,
    }),
    enabled: tab === 'transacciones',
    staleTime: 0,
  });
  const getValTrx = useCallback((r: Transaccion, key: string): string | number | null => {
    switch (key) {
      case 'fecha': return r.fecha;
      case 'origen': return r.origen;
      case 'medio': return r.medio;
      case 'venta': return r.venta;
      case 'caja': return r.caja;
      case 'cliente': return r.cliente;
      case 'monto': return Number(r.monto);
      case 'monto_cobrado': return r.monto_cobrado == null ? null : Number(r.monto_cobrado);
      case 'marca': return r.marca;
      case 'ultimos4': return r.ultimos4;
      case 'autorizacion': return r.autorizacion;
      case 'lote': return r.lote;
      case 'estado': return r.alerta ?? r.estado;
      default: return null;
    }
  }, []);
  // Reverso del cobro huérfano (895): devuelve la plata al cliente sin salir de
  // aquí. La BD se niega si el cobro respalda una venta — eso es nota de crédito.
  const reversar = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) => pasarelaReversar(id, motivo),
    onSuccess: (r) => (r.ya_reversado
      ? toast.info('Ese cobro ya estaba reversado')
      : toast.success(r.mensaje ?? 'Reverso solicitado')),
    onError: (e) => toast.error(getErrorMessage(e), { duration: 10000 }),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['pos-tarjeta-transacciones'] }); },
  });
  const pedirReverso = async (id: string, monto: number) => {
    const v = await dlg.pedir(`Reversar ${money(monto)} a la tarjeta del cliente`, [
      { key: 'motivo', label: 'Motivo', minimo: 3 },
    ], { detalle: 'Úsalo sólo si el cargo fue un error y NO hay venta que lo respalde.', aceptar: 'Reversar' });
    if (v?.motivo.trim()) reversar.mutate({ id, motivo: v.motivo.trim() });
  };

  const sortTrx = useSort(transacciones, getValTrx);
  const filasTrx = sortTrx.sorted;
  const tTrxMonto = filasTrx.reduce((s, r) => s + Number(r.monto), 0);
  const tTrxCobrado = filasTrx.reduce((s, r) => s + Number(r.monto_cobrado ?? 0), 0);
  // Cobros por un importe distinto al de la venta: dinero que falta o que sobra.
  // Reversado con la factura viva: lo más grave del listado.
  const trxReversado = transacciones.filter((r) => r.alerta === 'reversado_con_venta');
  const tReversado = trxReversado.reduce((s, r) => s + Number(r.monto), 0);
  const trxDescuadre = transacciones.filter((r) => r.alerta === 'monto_distinto');
  const tDescuadre = trxDescuadre.reduce((s, r) => s + Number(r.diferencia), 0);
  // Lo cobrado sin venta detrás se cuenta aparte: es lo único que urge.
  const trxSinVenta = transacciones.filter((r) => r.alerta === 'sin_venta');
  const tSinVenta = trxSinVenta.reduce((s, r) => s + Number(r.monto), 0);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <CreditCard className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Conciliación de tarjetas</h1>
          <p className="text-sm text-muted-foreground">Datafast / Medianet — cruce automático contra los pagos con tarjeta del POS.</p>
        </div>
      </div>

      {/* Toggle de secciones */}
      <div className="flex gap-2">
        <Button variant={tab === 'importaciones' ? 'default' : 'outline'} size="sm" onClick={() => setTab('importaciones')}>
          <FileSpreadsheet className="h-4 w-4 mr-1" /> Importaciones y detalle
        </Button>
        <Button variant={tab === 'depositos' ? 'default' : 'outline'} size="sm" onClick={() => setTab('depositos')}>
          <Landmark className="h-4 w-4 mr-1" /> Depósitos vs banco
        </Button>
        <Button variant={tab === 'transacciones' ? 'default' : 'outline'} size="sm" onClick={() => setTab('transacciones')}>
          <CreditCard className="h-4 w-4 mr-1" /> Transacciones con tarjeta
        </Button>
      </div>

      {tab === 'importaciones' && (<>
      {/* 1. Importar */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">1 · Importar liquidación</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Campo label="Adquirente *">
              <select className={fieldCls} value={adquirente} onChange={(e) => setAdquirente(e.target.value as 'datafast' | 'medianet')}>
                {ADQUIRENTES.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
              </select>
            </Campo>
            <Campo label="Archivo Excel (.xlsx) *">
              <input key={fileKey} type="file" accept=".xlsx,.xls" className={fieldCls}
                onChange={(e) => void onFile(e.target.files?.[0] ?? null)} />
            </Campo>
          </div>
          {parseando && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Analizando archivo…</div>}

          {filas.length > 0 && (
            <div className="space-y-3 rounded-md border p-3">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span><b>{filas.length}</b> fila(s) conciliadas en <span className="font-mono">{archivo}</span></span>
                <span>Bruto seleccionado: <b>{money(totalBruto)}</b></span>
                <span>Neto seleccionado: <b>{money(totalNeto)}</b></span>
              </div>

              {resumenComercios.length > 1 && (
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Comercios / terminales detectados — elige cuáles importar</span>
                    <div className="flex gap-2 text-xs">
                      <button className="text-primary hover:underline" onClick={() => toggleTodos(true)}>Marcar todos</button>
                      <button className="text-primary hover:underline" onClick={() => toggleTodos(false)}>Ninguno</button>
                    </div>
                  </div>
                  <div className="max-h-40 overflow-y-auto rounded border">
                    <table className="w-full text-xs">
                      <tbody>
                        {resumenComercios.map(([t, r]) => (
                          <tr key={t} className="border-b last:border-0">
                            <td className="py-1 pl-2 pr-2 w-8">
                              <input type="checkbox" checked={comercios[t] !== false} onChange={(e) => toggleComercio(t, e.target.checked)} />
                            </td>
                            <td className="py-1 pr-3 font-mono">{t}</td>
                            <td className="py-1 pr-3 text-right text-muted-foreground">{r.n} fila(s)</td>
                            <td className="py-1 pr-3 text-right font-mono tabular-nums">{money(r.bruto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button isLoading={importar.isPending} disabled={filasSeleccionadas.length === 0}
                  onClick={() => importar.mutate()}>
                  <Upload className="h-4 w-4 mr-1" /> Importar y conciliar ({filasSeleccionadas.length})
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2. Importaciones */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          Importaciones {fetchImp && <Loader2 className="h-4 w-4 animate-spin" />}
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="adquirente" sort={sortImp}>Adquirente</SortTh>
              <SortTh col="archivo" sort={sortImp}>Archivo</SortTh>
              <SortTh col="fecha" sort={sortImp}>Fecha</SortTh>
              <SortTh col="n_filas" sort={sortImp} align="right">Filas</SortTh>
              <SortTh col="total_bruto" sort={sortImp} align="right">Total bruto</SortTh>
              <SortTh col="total_neto" sort={sortImp} align="right">Total neto</SortTh>
              <SortTh col="conciliados" sort={sortImp} align="right">Conciliados</SortTh>
              <SortTh col="pendientes" sort={sortImp} align="right">Pendientes</SortTh>
              <SortTh col="estado" sort={sortImp}>Estado</SortTh>
            </tr></thead>
            <tbody>
              {filasImp.length === 0 && !fetchImp && (
                <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin importaciones registradas</td></tr>
              )}
              {filasImp.map((r, i) => (
                <tr key={r.id}
                  className={`cursor-pointer border-b last:border-0 hover:bg-muted/40 ${importacionSel?.id === r.id ? 'bg-primary/10' : ''}`}
                  onClick={() => setImportacionSel(r)}>
                  <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3 capitalize">{r.adquirente}</td>
                  <td className="py-1.5 pr-3 max-w-[16rem] truncate" title={r.archivo}>{r.archivo}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{fmtFecha(r.fecha)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{r.n_filas}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(r.total_bruto)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(r.total_neto)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-estable">{r.conciliados}</td>
                  <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-pendiente">{r.pendientes}</td>
                  <td className="py-1.5 pr-3 text-xs">{r.estado}</td>
                </tr>
              ))}
            </tbody>
            {filasImp.length > 0 && (
              <tfoot>
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={4}>Totales ({filasImp.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{tImpFilas}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tImpBruto)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tImpNeto)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      {/* 3. Detalle / tablero */}
      {importacionSel && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" /> Detalle — {importacionSel.archivo} {fetchLiq && <Loader2 className="h-4 w-4 animate-spin" />}
              {importacionSel.estado === 'contabilizado' && (
                <span className="rounded px-1.5 py-0.5 text-xs font-medium bg-estable-soft text-estable">Contabilizado</span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <select className={`${fieldCls} w-44 h-9`} value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
                {ESTADOS_LIQ.map((e) => <option key={e.v} value={e.v}>{e.l}</option>)}
              </select>
              <Button variant="outline" size="sm" isLoading={contabilizar.isPending} onClick={onContabilizar}>
                <BookText className="h-4 w-4 mr-1" />
                {importacionSel.estado === 'contabilizado' ? 'Re-contabilizar' : 'Contabilizar'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th>
                <SortTh col="fecha_tx" sort={sortLiq}>Fecha</SortTh>
                <SortTh col="lote" sort={sortLiq}>Lote</SortTh>
                <SortTh col="autorizacion" sort={sortLiq}>Autorización</SortTh>
                <SortTh col="ultimos4" sort={sortLiq}>Tarjeta</SortTh>
                <SortTh col="marca" sort={sortLiq}>Marca</SortTh>
                <SortTh col="terminal" sort={sortLiq}>Terminal</SortTh>
                <SortTh col="bruto" sort={sortLiq} align="right">Bruto</SortTh>
                <SortTh col="comision" sort={sortLiq} align="right">Comisión</SortTh>
                <SortTh col="retenciones" sort={sortLiq} align="right">Retenciones</SortTh>
                <SortTh col="neto" sort={sortLiq} align="right">Neto</SortTh>
                <SortTh col="diferencia" sort={sortLiq} align="right">Diferencia</SortTh>
                <SortTh col="estado" sort={sortLiq}>Estado</SortTh>
              </tr></thead>
              <tbody>
                {filasLiq.length === 0 && !fetchLiq && (
                  <tr><td colSpan={13} className="py-8 text-center text-muted-foreground">Sin liquidaciones con ese filtro</td></tr>
                )}
                {filasLiq.map((r, i) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{fmtFecha(r.fecha_tx)}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.lote ?? '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.autorizacion ?? '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.ultimos4 ? `•••• ${r.ultimos4}` : '—'}</td>
                    <td className="py-1.5 pr-3">{r.marca ?? '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.terminal ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(r.monto_bruto)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(r.comision)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(Number(r.ret_ir) + Number(r.ret_iva))}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums font-medium">{money(r.monto_neto)}</td>
                    <td className={`py-1.5 pr-3 text-right font-mono tabular-nums ${Number(r.diferencia) !== 0 ? 'text-pendiente' : ''}`}>{money(r.diferencia)}</td>
                    <td className="py-1.5 pr-3">
                      <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${ESTADO_LIQ_CLS[r.estado] ?? 'bg-muted text-muted-foreground'}`}>
                        {estadoLabel(r.estado)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              {filasLiq.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={7}>Totales ({filasLiq.length})</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tLBruto)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tLComision)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tLRet)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tLNeto)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      )}
      </>)}

      {/* Depósitos vs banco (Fase 2B) */}
      {tab === 'depositos' && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Landmark className="h-4 w-4" /> Depósitos vs banco {fetchDep && <Loader2 className="h-4 w-4 animate-spin" />}
            </CardTitle>
            <div className="flex flex-wrap items-end gap-2">
              <Campo label="Desde" className="w-36">
                <input type="date" className={fieldCls} value={depDesde} onChange={(e) => setDepDesde(e.target.value)} />
              </Campo>
              <Campo label="Hasta" className="w-36">
                <input type="date" className={fieldCls} value={depHasta} onChange={(e) => setDepHasta(e.target.value)} />
              </Campo>
              <Campo label="Adquirente" className="w-40">
                <select className={fieldCls} value={depAdquirente} onChange={(e) => setDepAdquirente(e.target.value)}>
                  {ADQUIRENTES_FILTRO.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
                </select>
              </Campo>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 overflow-x-auto">
            <p className="text-xs text-muted-foreground">
              Cruza el depósito neto del adquirente contra los créditos del estado de cuenta importado en Control Bancario.
              Si un depósito sale &quot;Pendiente&quot;, aún no aparece en el banco (o el estado de cuenta no se ha importado).
            </p>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th>
                <SortTh col="adquirente" sort={sortDep}>Adquirente</SortTh>
                <SortTh col="fecha_deposito" sort={sortDep}>Fecha depósito</SortTh>
                <SortTh col="n" sort={sortDep} align="right">N° trans.</SortTh>
                <SortTh col="bruto" sort={sortDep} align="right">Bruto</SortTh>
                <SortTh col="neto_esperado" sort={sortDep} align="right">Neto esperado</SortTh>
                <SortTh col="banco_fecha" sort={sortDep}>Fecha banco</SortTh>
                <SortTh col="banco_monto" sort={sortDep} align="right">Monto banco</SortTh>
                <SortTh col="banco_concepto" sort={sortDep}>Concepto banco</SortTh>
                <SortTh col="diferencia" sort={sortDep} align="right">Diferencia</SortTh>
                <SortTh col="estado" sort={sortDep}>Estado</SortTh>
              </tr></thead>
              <tbody>
                {filasDep.length === 0 && !fetchDep && (
                  <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Sin depósitos con ese filtro</td></tr>
                )}
                {filasDep.map((r, i) => (
                  <tr key={`${r.adquirente}-${r.fecha_deposito}-${i}`} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3 capitalize">{r.adquirente}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{fmtFecha(r.fecha_deposito)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{r.n}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(r.bruto)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums font-medium">{money(r.neto_esperado)}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{fmtFecha(r.banco_fecha)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{r.banco_monto == null ? '—' : money(r.banco_monto)}</td>
                    <td className="py-1.5 pr-3 max-w-[14rem] truncate" title={r.banco_referencia ? `Ref: ${r.banco_referencia}` : undefined}>
                      {r.banco_concepto ?? '—'}
                    </td>
                    <td className={`py-1.5 pr-3 text-right font-mono tabular-nums ${Number(r.diferencia) !== 0 ? 'text-pendiente' : ''}`}>{money(r.diferencia)}</td>
                    <td className="py-1.5 pr-3">
                      <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${ESTADO_DEP_CLS[r.estado] ?? 'bg-muted text-muted-foreground'}`}>
                        {ESTADO_DEP_LABEL[r.estado] ?? r.estado}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              {filasDep.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={4}>Totales ({filasDep.length})</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tDepBruto)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tDepNeto)}</td>
                    <td colSpan={1}></td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tDepBanco)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      )}
      {tab === 'transacciones' && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Transacciones con tarjeta {fetchTrx && <Loader2 className="h-4 w-4 animate-spin" />}
            </CardTitle>
            <div className="flex flex-wrap items-end gap-2">
              <Campo label="Desde" className="w-36">
                <input type="date" className={fieldCls} value={trxDesde} onChange={(e) => setTrxDesde(e.target.value)} />
              </Campo>
              <Campo label="Hasta" className="w-36">
                <input type="date" className={fieldCls} value={trxHasta} onChange={(e) => setTrxHasta(e.target.value)} />
              </Campo>
              <Campo label="Cómo se cobró" className="w-40">
                <select className={fieldCls} value={trxOrigen} onChange={(e) => setTrxOrigen(e.target.value)}>
                  <option value="">Todas</option>
                  <option value="datafono">Datáfono</option>
                  <option value="pasarela">Enlace / pasarela</option>
                </select>
              </Campo>
              <Campo label="Medio" className="w-36">
                <select className={fieldCls} value={trxMedio} onChange={(e) => setTrxMedio(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="deuna">De Una</option>
                </select>
              </Campo>
              <Campo label="Revisar" className="w-48">
                <select className={fieldCls} value={trxAlerta} onChange={(e) => setTrxAlerta(e.target.value)}>
                  {ALERTAS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
                </select>
              </Campo>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 overflow-x-auto">
            <p className="text-xs text-muted-foreground">
              Todo lo cobrado con tarjeta desde el sistema, esté o no liquidado por el banco: el datáfono y el enlace de pago.
              Aquí se verifican las autorizaciones antes de que llegue el archivo del adquirente.
            </p>

            {trxSinVenta.length > 0 && (
              <div className="rounded-md border border-urgente/40 bg-urgente-soft p-3 text-sm">
                <p className="font-medium text-urgente">
                  {trxSinVenta.length} cobro(s) autorizado(s) por {money(tSinVenta)} sin venta que los respalde.
                </p>
                <p className="text-xs text-muted-foreground">
                  La tarjeta del cliente ya fue cargada pero no existe la venta. Registra la venta, o
                  usa <strong>Reversar</strong> para devolverle el dinero.
                </p>
              </div>
            )}

            {trxReversado.length > 0 && (
              <div className="rounded-md border border-urgente/40 bg-urgente-soft p-3 text-sm">
                <p className="font-medium text-urgente">
                  {trxReversado.length} cobro(s) por {money(tReversado)} fueron REVERSADOS y su factura sigue viva.
                </p>
                <p className="text-xs text-muted-foreground">
                  Al cliente ya se le devolvió el dinero, pero el comprobante sigue autorizado en el SRI
                  cobrándoselo. Hay que emitir la <strong>nota de crédito</strong> correspondiente.
                </p>
              </div>
            )}

            {trxDescuadre.length > 0 && (
              <div className="rounded-md border border-urgente/40 bg-urgente-soft p-3 text-sm">
                <p className="font-medium text-urgente">
                  {trxDescuadre.length} cobro(s) por un importe distinto al de la venta · diferencia {money(tDescuadre)}.
                </p>
                <p className="text-xs text-muted-foreground">
                  Lo que pasó por la tarjeta no es lo que la venta dice que se pagó. Suele ser el recargo por
                  comisión de tarjeta cobrado de menos, o un importe cambiado después del cobro.
                </p>
              </div>
            )}

            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th>
                <SortTh col="fecha" sort={sortTrx}>Fecha</SortTh>
                <SortTh col="origen" sort={sortTrx}>Cobro</SortTh>
                <SortTh col="medio" sort={sortTrx}>Medio</SortTh>
                <SortTh col="venta" sort={sortTrx}>Venta</SortTh>
                <SortTh col="caja" sort={sortTrx}>Caja</SortTh>
                <SortTh col="cliente" sort={sortTrx}>Cliente</SortTh>
                <SortTh col="monto" sort={sortTrx} align="right">Monto venta</SortTh>
                <SortTh col="monto_cobrado" sort={sortTrx} align="right">Cobrado a la tarjeta</SortTh>
                <SortTh col="marca" sort={sortTrx}>Marca</SortTh>
                <SortTh col="ultimos4" sort={sortTrx}>Tarjeta</SortTh>
                <SortTh col="autorizacion" sort={sortTrx}>Autorización</SortTh>
                <SortTh col="lote" sort={sortTrx}>Lote</SortTh>
                <SortTh col="estado" sort={sortTrx}>Estado</SortTh>
                <th className="py-2 pr-3"></th>
              </tr></thead>
              <tbody>
                {filasTrx.length === 0 && !fetchTrx && (
                  <tr><td colSpan={15} className="py-8 text-center text-muted-foreground">Sin transacciones con tarjeta en ese rango</td></tr>
                )}
                {filasTrx.map((r, i) => (
                  <tr key={r.id} className={`border-b last:border-0 hover:bg-muted/40 ${r.alerta === 'sin_venta' || r.alerta === 'reversado_con_venta' ? 'bg-urgente-soft/40' : ''}`}>
                    <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(r.fecha).toLocaleDateString(LOCALE)}</td>
                    <td className="py-1.5 pr-3">
                      {r.origen === 'pasarela' ? 'Enlace' : 'Datáfono'}
                      {r.modulo === 'restaurant' && <span className="ml-1 text-xs text-muted-foreground">(salón)</span>}
                    </td>
                    <td className="py-1.5 pr-3">
                      {r.medio === 'deuna'
                        ? <span className="rounded bg-estable-soft px-1.5 py-0.5 text-xs font-medium text-estable">De Una</span>
                        : <span className="text-xs text-muted-foreground">Tarjeta</span>}
                    </td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.venta ?? <span className="text-urgente">— sin venta —</span>}</td>
                    <td className="py-1.5 pr-3">{r.caja ?? '—'}</td>
                    <td className="py-1.5 pr-3 max-w-[14rem] truncate" title={r.cliente ?? undefined}>{r.cliente ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums font-medium">{money(r.monto)}</td>
                    <td className={`py-1.5 pr-3 text-right font-mono tabular-nums ${Number(r.diferencia) !== 0 ? 'font-semibold text-urgente' : ''}`}
                        title={Number(r.diferencia) !== 0 ? `Diferencia: ${money(r.diferencia)}` : undefined}>
                      {r.monto_cobrado == null ? '—' : money(r.monto_cobrado)}
                    </td>
                    <td className="py-1.5 pr-3 capitalize">{r.marca ?? '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.ultimos4 ? `····${r.ultimos4}` : '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.autorizacion ?? '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.lote ?? '—'}</td>
                    <td className="py-1.5 pr-3">
                      {r.alerta
                        ? <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${ALERTA_CLS[r.alerta]}`}>{ALERTA_LABEL[r.alerta]}</span>
                        : <span className="rounded bg-estable-soft px-1.5 py-0.5 text-xs font-medium text-estable">
                            {r.conciliado ? 'Conciliado' : r.estado}
                          </span>}
                    </td>
                    <td className="py-1.5 pr-3">
                      {r.alerta === 'sin_venta' && r.origen === 'pasarela' && (
                        <Button size="sm" variant="outline" isLoading={reversar.isPending}
                                onClick={() => void pedirReverso(r.id, Number(r.monto))}>
                          Reversar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {filasTrx.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 pr-3" colSpan={7}>Totales ({filasTrx.length})</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tTrxMonto)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tTrxCobrado)}</td>
                    <td colSpan={6}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
